/**
 * Calendar: choose which days count as sessions.
 *
 * Two controls, in the order people reach for them: the weekdays a group normally meets, and
 * then the individual dates that deviate — a holiday, a make-up session. The month grid shows
 * the result of both, so the schedule is never described in one place and seen in another.
 *
 * Days can be painted by dragging, because the common edit is a run of days (a holiday week,
 * a block of exam dates) rather than a single one.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';

import {
  DAYS_PER_WEEK,
  addDays,
  addMonths,
  eachDay,
  endOfMonth,
  parseIso,
  startOfMonth,
  weekdayOf,
} from '../../shared/dates.js';
import { isSessionDate } from '../../shared/schedule.js';
import { OverrideKind, type IsoDate, type Weekday } from '../../shared/types.js';
import { setTerm, setWeekdays, toggleSessionDate } from '../data/mutations.js';
import { useStore } from '../data/context.js';
import { useProjectDerived } from '../data/derived.js';
import { formatLongDate, formatMonth, plural } from '../ui/format.js';
import { ChevronLeftIcon, ChevronRightIcon } from '../ui/icons.js';
import { Button, EmptyState, IconButton, TextField } from '../ui/primitives.js';
import { ProjectFrame } from '../ui/Shell.js';

/** Weekday the grid starts on. Monday matches the school and club week these rosters follow. */
const FIRST_WEEKDAY: Weekday = 1;

/** A reference Sunday, used to render weekday names in the viewer's locale. */
const REFERENCE_SUNDAY = '2024-01-07';

/** Weekday numbers in grid order, starting at `FIRST_WEEKDAY`. */
const GRID_WEEKDAYS: Weekday[] = Array.from(
  { length: DAYS_PER_WEEK },
  (_unused, offset) => ((FIRST_WEEKDAY + offset) % DAYS_PER_WEEK) as Weekday,
);

/** Attribute the painter reads off a day cell to learn which date it covers. */
const DATE_ATTRIBUTE = 'data-calendar-date';

/** What a mark on a day cell means, drawn the way the cell draws it. */
const MARKERS: readonly { label: string; swatch: string }[] = [
  { label: 'Changed by hand', swatch: 'inline-block h-2 w-2 rounded-full bg-accent' },
  { label: 'Has notes', swatch: 'inline-block h-2 w-2 rounded-full bg-brand-graphic' },
  { label: 'Today', swatch: 'inline-block h-3.5 w-3.5 rounded-[5px] ring-2 ring-accent' },
];

/** Returns the localized name of a weekday. */
function weekdayName(weekday: Weekday, style: 'short' | 'long'): string {
  return parseIso(addDays(REFERENCE_SUNDAY, weekday)).toLocaleDateString(undefined, {
    weekday: style,
    timeZone: 'UTC',
  });
}

/** Lists the days shown in the month grid, padded to whole weeks. */
function gridDays(month: IsoDate): IsoDate[] {
  const first = startOfMonth(month);
  const last = endOfMonth(month);
  const leading = (weekdayOf(first) - FIRST_WEEKDAY + DAYS_PER_WEEK) % DAYS_PER_WEEK;
  const trailing = (FIRST_WEEKDAY + DAYS_PER_WEEK - 1 - weekdayOf(last) + DAYS_PER_WEEK) % DAYS_PER_WEEK;
  return eachDay(addDays(first, -leading), addDays(last, trailing));
}

/** The date under a pointer, or `null` when the pointer is not over a day cell. */
function dateUnderPointer(clientX: number, clientY: number): IsoDate | null {
  const element = document.elementFromPoint(clientX, clientY);
  const cell = element?.closest(`[${DATE_ATTRIBUTE}]`) ?? null;
  return cell === null ? null : cell.getAttribute(DATE_ATTRIBUTE);
}

/** Handlers that turn a drag across the month grid into a run of schedule edits. */
interface Painter {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: { key: string; preventDefault: () => void }, date: IsoDate) => void;
}

/**
 * Paint-selection over the month grid.
 *
 * The first day touched decides the direction of the whole stroke — if it was a session it
 * becomes one that is skipped, and every day the stroke reaches follows suit — so a drag can
 * only ever add or only ever remove, never flip days back and forth under the finger.
 *
 * @param apply invoked once per date with the state the stroke is painting.
 */
function useDatePainter(
  isSession: (date: IsoDate) => boolean,
  apply: (date: IsoDate, wanted: boolean) => void,
): Painter {
  const stroke = useRef<{ wanted: boolean; painted: Set<IsoDate> } | null>(null);

  useEffect(() => {
    const end = (): void => { stroke.current = null; };
    globalThis.addEventListener('pointerup', end);
    globalThis.addEventListener('pointercancel', end);
    return () => {
      globalThis.removeEventListener('pointerup', end);
      globalThis.removeEventListener('pointercancel', end);
    };
  }, []);

  const paint = useCallback((date: IsoDate): void => {
    const current = stroke.current;
    if (current === null || current.painted.has(date)) return;
    current.painted.add(date);
    if (isSession(date) !== current.wanted) apply(date, current.wanted);
  }, [isSession, apply]);

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>): void => {
    const date = dateUnderPointer(event.clientX, event.clientY);
    if (date === null) return;
    stroke.current = { wanted: !isSession(date), painted: new Set() };
    paint(date);
  }, [isSession, paint]);

  const onPointerMove = useCallback((event: PointerEvent<HTMLElement>): void => {
    if (stroke.current === null) return;
    const date = dateUnderPointer(event.clientX, event.clientY);
    if (date !== null) paint(date);
  }, [paint]);

  const onKeyDown = useCallback((event: { key: string; preventDefault: () => void }, date: IsoDate): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    apply(date, !isSession(date));
  }, [isSession, apply]);

  return { onPointerDown, onPointerMove, onKeyDown };
}

/** Renders the schedule editor. */
export function CalendarPage(): ReactNode {
  const { projectId } = useParams();
  const store = useStore();
  const derived = useProjectDerived(projectId);
  const [month, setMonth] = useState<IsoDate | null>(null);
  const [showTerm, setShowTerm] = useState(false);

  const project = derived?.project;
  const overrides = derived?.overrides ?? new Map();
  const shownMonth = month ?? (derived === null ? '' : startOfMonth(derived.day));

  const isSession = useCallback(
    (date: IsoDate): boolean => (project === undefined ? false : isSessionDate(project, date, overrides)),
    [project, overrides],
  );

  const applyToggle = useCallback((date: IsoDate): void => {
    if (project === undefined) return;
    void toggleSessionDate(store, project, overrides, date);
  }, [store, project, overrides]);

  const painter = useDatePainter(isSession, applyToggle);

  const days = useMemo(() => (shownMonth === '' ? [] : gridDays(shownMonth)), [shownMonth]);
  const bounds = useMemo(
    () => (shownMonth === ''
      ? { start: '', end: '' }
      : { start: startOfMonth(shownMonth), end: endOfMonth(shownMonth) }),
    [shownMonth],
  );
  const sessionCount = useMemo(
    () => (bounds.start === '' ? 0 : eachDay(bounds.start, bounds.end).filter(isSession).length),
    [bounds, isSession],
  );

  if (derived === null || project === undefined) {
    return <ProjectFrame><EmptyState message="This project is not on this device." /></ProjectFrame>;
  }

  const toggleWeekday = (weekday: Weekday): void => {
    const next = project.weekdays.includes(weekday)
      ? project.weekdays.filter((day) => day !== weekday)
      : [...project.weekdays, weekday];
    void setWeekdays(store, project.id, next);
  };

  return (
    <ProjectFrame>
      <section className="px-4 py-5">
        <p className="text-base text-ink-2">Attendance is checked on these weekdays.</p>
        <div className="mt-3 grid grid-cols-7 gap-1.5">
          {GRID_WEEKDAYS.map((weekday) => {
            const on = project.weekdays.includes(weekday);
            return (
              <button
                key={weekday}
                type="button"
                aria-pressed={on}
                onClick={() => toggleWeekday(weekday)}
                className={`h-11 rounded-control border-2 text-base font-semibold transition-colors
                  ${on
                    ? 'border-brand bg-brand text-on-brand'
                    : 'border-line-strong bg-transparent text-ink-2 hover:border-brand hover:text-brand-text'}`}
              >
                <span aria-hidden="true">{weekdayName(weekday, 'short')}</span>
                <span className="sr-only">{weekdayName(weekday, 'long')}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="border-t border-line px-4 py-5">
        <div className="flex items-center justify-between gap-2">
          <IconButton label="Previous month" onClick={() => setMonth(addMonths(shownMonth, -1))}>
            <ChevronLeftIcon size={22} />
          </IconButton>
          <p className="text-title font-bold">{formatMonth(shownMonth)}</p>
          <IconButton label="Next month" onClick={() => setMonth(addMonths(shownMonth, 1))}>
            <ChevronRightIcon size={22} />
          </IconButton>
        </div>

        <div className="mt-4 grid grid-cols-7 gap-1">
          {GRID_WEEKDAYS.map((weekday) => (
            <div key={weekday} className="pb-1 text-center text-tiny font-semibold text-ink-3">
              <span aria-hidden="true">{weekdayName(weekday, 'short')}</span>
              <span className="sr-only">{weekdayName(weekday, 'long')}</span>
            </div>
          ))}
        </div>

        <div
          className="grid grid-cols-7 gap-1 select-none"
          // Vertical panning still scrolls the page; a sideways drag paints instead, which is
          // the gesture that matches selecting a run of days.
          style={{ touchAction: 'pan-y' }}
          onPointerDown={painter.onPointerDown}
          onPointerMove={painter.onPointerMove}
        >
          {days.map((date) => {
            const inMonth = date >= bounds.start && date <= bounds.end;
            const session = isSession(date);
            const override = overrides.get(date);
            const written = derived.notes.dates.has(date);
            const isToday = date === derived.day;
            return (
              <button
                key={date}
                type="button"
                {...{ [DATE_ATTRIBUTE]: date }}
                aria-pressed={session}
                aria-label={`${formatLongDate(date)}${session ? ', session' : ', no session'}`
                  + `${written ? ', has notes' : ''}`}
                onKeyDown={(event) => painter.onKeyDown(event, date)}
                className={`relative flex h-12 items-center justify-center rounded-control border-2 text-base
                  transition-colors
                  ${session
                    ? 'border-brand bg-brand-soft font-bold text-brand-ink'
                    : 'border-transparent font-normal text-ink-3 hover:border-line-strong hover:bg-sunken'}
                  ${inMonth ? '' : 'opacity-40'}
                  ${isToday ? 'ring-2 ring-accent ring-offset-1 ring-offset-page' : ''}`}
              >
                <span className="tabular pointer-events-none">{Number(date.slice(8))}</span>
                {override === undefined ? null : (
                  <span
                    aria-hidden="true"
                    title={override === OverrideKind.Added ? 'Added by hand' : 'Skipped by hand'}
                    className="pointer-events-none absolute right-1 bottom-1 h-1.5 w-1.5 rounded-full bg-accent"
                  />
                )}
                {written ? (
                  <span
                    aria-hidden="true"
                    title="Something is written down for this day"
                    className="pointer-events-none absolute bottom-1 left-1 h-1.5 w-1.5 rounded-full
                      bg-brand-graphic"
                  />
                ) : null}
              </button>
            );
          })}
        </div>

        <p className="mt-4 text-tiny text-ink-2">
          {sessionCount} {plural(sessionCount, 'session', 'sessions')} in {formatMonth(shownMonth)}.
          Drag across days to change a run at once.
        </p>
        {/* The three markers a day can carry, named rather than described in a sentence. */}
        <ul className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
          {MARKERS.map((marker) => (
            <li key={marker.label} className="inline-flex items-center gap-1.5 text-tiny text-ink-3">
              <span aria-hidden="true" className={marker.swatch} />
              {marker.label}
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-line px-4 py-5">
        <button
          type="button"
          onClick={() => setShowTerm(!showTerm)}
          className="inline-flex h-11 items-center gap-2 rounded-control border border-line-strong
            px-4 text-base font-semibold text-ink transition-colors hover:bg-sunken"
          aria-expanded={showTerm}
        >
          {showTerm ? 'Hide the term dates' : 'Limit the schedule to a term'}
        </button>
        {showTerm ? (
          <div className="mt-4 flex flex-col gap-4 sm:flex-row">
            <div className="flex-1">
              <TextField
                label="First day"
                type="date"
                value={project.termStart ?? ''}
                onChange={(value) => void setTerm(store, project.id, value === '' ? null : value, project.termEnd)}
                hint="Leave empty to start from the day the project was created."
              />
            </div>
            <div className="flex-1">
              <TextField
                label="Last day"
                type="date"
                value={project.termEnd ?? ''}
                onChange={(value) => void setTerm(store, project.id, project.termStart, value === '' ? null : value)}
                hint="Leave empty to keep running."
              />
            </div>
          </div>
        ) : null}
        {project.termStart === null && project.termEnd === null ? null : (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <p className="text-tiny text-ink-2">
              Sessions run
              {project.termStart === null ? ' until ' : ` from ${formatLongDate(project.termStart)} `}
              {project.termEnd === null ? 'with no end date' : `to ${formatLongDate(project.termEnd)}`}.
            </p>
            <Button tone="quiet" className="h-9" onClick={() => void setTerm(store, project.id, null, null)}>
              Clear
            </Button>
          </div>
        )}
      </section>
    </ProjectFrame>
  );
}
